import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import AppShell from "@/components/layout/AppShell";
import { ScrollText, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const ACTION_COLOR = (a) => {
  if (a.includes("delete")) return "text-red-400";
  if (a.includes("create") || a.includes("import")) return "text-green-400";
  if (a.includes("update")) return "text-orange-400";
  if (a.includes("login") || a.includes("auth")) return "text-blue-400";
  return "text-muted-foreground";
};

export default function AuditLog() {
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["audit"],
    queryFn: async () => (await api.get("/audit", { params: { limit: 300 } })).data,
  });
  const { data: chain } = useQuery({
    queryKey: ["audit-verify"],
    queryFn: async () => (await api.get("/audit/verify")).data,
  });

  return (
    <AppShell title="Journal d'audit" subtitle="Append-only · hash-chaîné · immuable"
      actions={
        chain && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border text-sm font-mono ${chain.valid ? "border-green-500/40 bg-green-500/10 text-green-400" : "border-red-500/40 bg-red-500/10 text-red-400"}`}
            data-testid="chain-status">
            {chain.valid ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
            {chain.valid ? `Chaîne intègre · ${chain.entries} entrées` : `Chaîne compromise @${chain.broken_at}`}
          </div>
        )
      }>
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</div>
      ) : (
        <div className="relative pl-6" data-testid="audit-timeline">
          <div className="absolute left-2 top-0 bottom-0 w-px bg-border" aria-hidden="true" />
          {entries.map((e) => (
            <div key={e.hash} className="relative pb-6" data-testid={`audit-entry-${e.seq}`}>
              <div className="absolute -left-[17px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-background" />
              <div className="border border-border bg-card rounded-sm p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs text-muted-foreground">#{e.seq}</span>
                    <span className={`font-mono text-sm font-medium ${ACTION_COLOR(e.action)}`}>{e.action}</span>
                    <span className="text-sm text-muted-foreground">{e.resource}</span>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{new Date(e.ts).toLocaleString("fr-FR")}</span>
                </div>
                <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
                  <Badge variant="outline" className="rounded-sm font-mono">{e.actor}</Badge>
                  <span className="font-mono">ip {e.ip}</span>
                  <span className="font-mono truncate max-w-[240px]" title={e.hash}>hash {e.hash.slice(0, 16)}…</span>
                </div>
              </div>
            </div>
          ))}
          {entries.length === 0 && <p className="text-sm text-muted-foreground">Aucune entrée.</p>}
        </div>
      )}
    </AppShell>
  );
}
